import { onScopeDispose, watch, type Ref } from 'vue';

type AuthSessionTabMessage = {
  type: 'session';
  sourceId: string;
  identityKey: string;
  organizationId: string | null;
};

type AuthLogoutTabMessage = {
  type: 'logout';
  sourceId: string;
};

type AuthTabMessage = AuthSessionTabMessage | AuthLogoutTabMessage;

const CHANNEL_NAME = 'peanuds:auth-identity';
const SOURCE_ID = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

const isAuthTabMessage = (value: unknown): value is AuthTabMessage => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const message = value as Record<string, unknown>;

  if (typeof message.sourceId !== 'string' || message.sourceId.length === 0) {
    return false;
  }

  if (message.type === 'logout') {
    return true;
  }

  return (
    message.type === 'session' &&
    typeof message.identityKey === 'string' &&
    message.identityKey.length > 0 &&
    (message.organizationId === null ||
      (typeof message.organizationId === 'string' && message.organizationId.length > 0))
  );
};

export const publishAuthLogoutToTabs = (): void => {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'logout', sourceId: SOURCE_ID } satisfies AuthLogoutTabMessage);
  channel.close();
};

export const useAuthIdentityTabSync = (
  identityKey: Readonly<Ref<string | null>>,
  organizationId: Readonly<Ref<string | null>>,
): void => {
  const channel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  let reloadRequested = false;
  let pendingRemoteLogout = false;
  let pendingRemoteSession: AuthSessionTabMessage | null = null;

  const reloadPage = (): void => {
    if (reloadRequested) {
      return;
    }

    reloadRequested = true;
    globalThis.location.reload();
  };

  const reconcileRemoteState = (): void => {
    if (identityKey.value === null) {
      return;
    }

    if (pendingRemoteLogout) {
      reloadPage();
      return;
    }

    if (pendingRemoteSession === null) {
      return;
    }

    if (pendingRemoteSession.identityKey !== identityKey.value) {
      reloadPage();
      return;
    }

    if (pendingRemoteSession.organizationId !== null && organizationId.value === null) {
      return;
    }

    if (
      pendingRemoteSession.organizationId !== null &&
      pendingRemoteSession.organizationId !== organizationId.value
    ) {
      reloadPage();
      return;
    }

    pendingRemoteSession = null;
  };

  const handleMessage = ({ data }: MessageEvent<unknown>): void => {
    if (!isAuthTabMessage(data) || data.sourceId === SOURCE_ID) {
      return;
    }

    if (data.type === 'logout') {
      pendingRemoteLogout = true;
    } else {
      pendingRemoteSession = data;
    }

    reconcileRemoteState();
  };

  channel?.addEventListener('message', handleMessage);

  watch(
    [identityKey, organizationId],
    ([nextIdentityKey, nextOrganizationId]) => {
      if (nextIdentityKey === null) {
        return;
      }

      reconcileRemoteState();

      if (reloadRequested) {
        return;
      }

      channel?.postMessage({
        type: 'session',
        sourceId: SOURCE_ID,
        identityKey: nextIdentityKey,
        organizationId: nextOrganizationId,
      } satisfies AuthSessionTabMessage);
    },
    { flush: 'sync', immediate: true },
  );

  onScopeDispose(() => {
    channel?.removeEventListener('message', handleMessage);
    channel?.close();
  });
};
